'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="The Slow Cooker Liner Is Not a Sandwich Bag"
      author="MicroPlastics Research Desk"
      date="August 25, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/food.webp"
      heroImageAlt="Slow cooker liners are made of nylon rather than polyethylene, engineered for heat like an oven bag, but manufacturers do not disclose which polyamide grade is used"
    >
      <BlogLastUpdated iso="2026-09-21" formatted="September 21, 2026" />

      <BlogTLDR>
        <strong>A slow cooker liner is not the same plastic as a food storage
        bag, and the difference matters more than the panic does.</strong>{' '}
        Reynolds states first-party that its liners are made of{' '}
        <em>&ldquo;a blend of nylon&rdquo;</em> — the same polymer family as an
        oven bag — that it has used for over 30 years, BPA-free, FDA-compliant
        and rated to <strong>400&nbsp;°F</strong>. Nylon is in there{' '}
        <em>because</em> of the heat, not in spite of it. Polyethylene, the
        Ziploc polymer, would not survive the job.{' '}
        <strong>But there is a real gap, and it is not the one the internet is
        worried about.</strong> The migrant with an actual legal limit here is a
        leftover monomer, and which monomer depends entirely on which nylon it
        is. <strong>Nylon 6 is polymerised from caprolactam</strong>, which
        carries an EU specific migration limit of 15 mg/kg.{' '}
        <strong>Nylon 66 is made from hexamethylenediamine and adipic acid</strong>,
        although a Japanese government survey still found small caprolactam
        residues in every nylon-66 kitchen item it tested.{' '}
        &ldquo;A blend of nylon&rdquo; does not tell you which question applies
        to your dinner. What has been measured, in the closest analogue anyone
        has published — 50 retail foods sold packaged in nylon-6 —{' '}
        <strong>found caprolactam in 9 samples at 2.8&ndash;13 mg/kg, every
        single one below the legal limit</strong>, and found it only in products
        heated inside their packaging.{' '}
        <strong>Nobody has measured a slow cooker liner itself.</strong> The
        crock is glazed ceramic and is not the issue. This is a low-dose,
        regulated-monomer question with an information gap at the centre of it —
        not a microplastics story.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>Liners are nylon, not polyethylene.</strong> Reynolds says so directly: &ldquo;a blend of nylon&rdquo;, BPA-free, FDA-compliant, and the same material family as oven bags.</>,
          <><strong>Nylon is a heat material.</strong> Oven bags run to 400&nbsp;°F and beyond. A slow cooker on high sits near 100&nbsp;°C. The film is not near its limit — which is the opposite of the usual assumption.</>,
          <><strong>The real question is monomer migration, not shedding.</strong> There is no particle count for slow cooker liners because nobody has produced one.</>,
          <><strong>Which nylon is undisclosed, and it changes the question.</strong> Nylon 6 comes from caprolactam; nylon 66 comes from hexamethylenediamine and adipic acid, though small caprolactam residues have still been measured in nylon-66 kitchenware. Both release cyclic oligomers into hot, fatty food.</>,
          <><strong>Caprolactam has an EU limit of 15 mg/kg</strong> — a real regulated substance with a real threshold, which is more than can be said for most things discussed in this space.</>,
          <><strong>In the closest measured analogue, everything passed.</strong> Of 50 retail foods packaged in nylon-6, caprolactam was confirmed in 9 at 2.8&ndash;13 mg/kg and every result was below the limit, averaging 2.6 mg/kg across all 50.</>,
          <><strong>Heat inside the pack is the trigger.</strong> Every sample in that survey with detectable migration came from an application where the food was heated in its packaging — which is exactly what a liner is.</>,
          <><strong>Duration is the untested variable.</strong> A slow cooker runs 6&ndash;10 hours. Nothing in the published analogues runs that long, so the honest answer is that the specific case is unmeasured.</>,
          <><strong>The appliance is fine.</strong> A glazed ceramic crock is one of the more inert surfaces in a kitchen. The liner is an optional accessory, and going without costs nothing but a wash.</>,
        ]}
      />

      <KeyStats
        heading="The numbers, and where each one comes from"
        stats={[
          { value: 'Nylon', label: 'what Reynolds says its slow cooker liners are made of', context: '“a blend of nylon”, used by the manufacturer for over 30 years, BPA-free and FDA-compliant for cooking', source: 'Reynolds Brands (manufacturer, first-party)', sourceUrl: 'https://www.reynoldsbrands.com/products/cooking-bag-liners/slow-cooker-liners' },
          { value: '400 °F', label: 'heat rating stated for the liner material', context: 'roughly 204 °C, against a slow cooker on high that sits near 100 °C — the film is not close to its limit', source: 'Reynolds Kitchens product specification', sourceUrl: 'https://www.reynoldsbrands.com/products/cooking-bag-liners/slow-cooker-liners' },
          { value: '15 mg/kg', label: 'EU specific migration limit for caprolactam', context: 'the nylon 6 monomer — applicable only if the liner is nylon 6 rather than nylon 66, which is not disclosed', source: 'Commission Regulation (EU) No 10/2011', sourceUrl: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02011R0010-20200923' },
          { value: '9 of 50', label: 'retail foods in nylon-6 packaging with confirmed caprolactam migration', context: 'range 2.8–13 mg/kg, all below the 15 mg/kg limit; average across all 50 samples was 2.6 mg/kg', source: 'Food Additives & Contaminants 21(12), 2004', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/15799563/' },
          { value: 'Zero', label: 'published measurements of a slow cooker liner specifically', context: 'the analogues are food packaged and heated in nylon; the 6–10 hour case has not been tested', source: 'MicroPlastics Research Desk literature check, August 2026', sourceUrl: 'https://microplasticsapp.com/methodology' },
        ]}
      />

      <h2>The short answer</h2>
      <p>
        The instinctive reaction to a slow cooker liner is that you are
        simmering food in a plastic bag for eight hours, and that this must be
        roughly the worst thing you could do in a kitchen. It is a reasonable
        instinct and it gets the material wrong.
      </p>
      <p>
        The bag in the box is nylon. It is the same family of polymer as a
        turkey oven bag, and it is there precisely because nylon tolerates heat
        that other films do not. A polyethylene bag — the material of a{' '}
        <Link href="/blog/do-ziploc-bags-release-microplastics" className="text-forest underline">Ziploc</Link>{' '}
        — would deform long before a roasting bag noticed. Reynolds rates the
        liner material to 400&nbsp;°F. A slow cooker on high tops out around
        boiling. In engineering terms the film is loafing.
      </p>
      <p>
        So the honest question is not &ldquo;will it melt&rdquo;. It is what
        comes off a polyamide film held against hot, fatty, slightly acidic food
        for the better part of a working day. And that question has a real
        answer, a real regulatory limit, and a genuine hole in the middle of it.
      </p>

      <h2>What the manufacturer actually says</h2>
      <p>
        We take brand claims from the brand&rsquo;s own materials rather than
        from third-party listings, so here is Reynolds, verbatim:{' '}
        <em>&ldquo;The liners are made of a special BPA-free material suitable
        for cooking foods in slow cookers on high, low, or warm settings.
        Reynolds has safely used this blend of nylon for over 30 years.&rdquo;</em>{' '}
        And on compliance:{' '}
        <em>&ldquo;Slow Cooker Liners are BPA-free and FDA-compliant for
        cooking.&rdquo;</em>
      </p>
      <p>
        Two things in that are worth sitting with. The first is that BPA-free is
        close to meaningless here, and not because the claim is untrue — it is
        true. Bisphenol A is a polycarbonate and epoxy-resin issue. Nylon was
        never going to contain it. Saying a polyamide film is BPA-free is like
        advertising that a wooden spoon is gluten-free, and we have written
        about{' '}
        <Link href="/blog/is-bpa-free-plastic-safe" className="text-forest underline">how much work that phrase does</Link>{' '}
        elsewhere.
      </p>
      <p>
        The second is the handling instruction, which is a structural warning
        rather than a chemical one:{' '}
        <em>&ldquo;At no time should the liner be lifted from the bowl with food
        inside.&rdquo;</em>{' '}
        Reynolds also distinguishes the liners from its oven bags, noting they
        are <em>&ldquo;specifically tested and designed to guarantee performance
        only when used as a slow cooker liner&rdquo;</em> — which is a useful
        correction to the widespread claim that the two products are simply
        different sizes of the same thing.
      </p>

      <h2>The gap: which nylon is it?</h2>
      <p>
        This is the part of the story that has not been told, and it is the
        reason this page exists.
      </p>
      <p>
        &ldquo;Nylon&rdquo; is not one material. The two that dominate food
        contact are chemically unrelated at the monomer level:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Nylon 6</strong> is polymerised from a single monomer,{' '}
          <strong>&epsilon;-caprolactam</strong>. Residual caprolactam can
          migrate out of the finished film, and the EU sets a specific migration
          limit of <strong>15 mg/kg</strong> for it.
        </li>
        <li>
          <strong>Nylon 66</strong> is made from{' '}
          <strong>hexamethylenediamine and adipic acid</strong>, not from
          caprolactam. It also absorbs less moisture and holds up better at
          temperature, which is why it is often preferred where hot water is
          present. But &ldquo;not made from&rdquo; is not the same as &ldquo;never
          contains&rdquo;: Japan&rsquo;s National Institute of Health Sciences
          found a small amount of caprolactam in <strong>all 15 nylon-66
          kitchen products</strong> it analysed (Mutsuga et al., 2010).
        </li>
      </ul>
      <p>
        Reynolds says &ldquo;a blend of nylon&rdquo;. Industry descriptions of
        oven bags say they are made from food-grade nylon,{' '}
        <em>often nylon 6 or nylon 66</em>. Nobody in the consumer-facing chain
        states which, and a blend could mean both.
      </p>
      <p>
        The consequence is unusual and worth stating plainly:{' '}
        <strong>you cannot determine from any available information which
        migration question applies to the product in your kitchen.</strong> If
        it is nylon 66, caprolactam becomes a minor residue question rather
        than the main one. If it is nylon 6, it is the most relevant thing
        there is. Either way, the larger migrants from hot nylon into fatty
        food are <strong>cyclic oligomers</strong>, for which Germany&rsquo;s
        BfR recommends keeping migration below 5 mg/kg of food; see{' '}
        <Link href="/blog/are-oven-bags-safe-for-turkey" className="text-forest underline">what the oven-bag studies measured in roast chicken</Link>. That is not a
        scandal — undisclosed polymer grades are ordinary in food packaging —
        but it does mean anyone giving you a confident number for slow cooker
        liners is working from an assumption they have not told you about.
      </p>

      <h2>What has actually been measured</h2>
      <p>
        There is no published measurement of a slow cooker liner. What exists is
        the closest analogue: food sold packaged in nylon-6 and then heated
        inside that packaging.
      </p>
      <p>
        A survey published in <em>Food Additives &amp; Contaminants</em> analysed{' '}
        <strong>50 retail foodstuffs packaged in nylon-6</strong>. Caprolactam
        was confirmed in <strong>nine of the 50</strong>, in the range{' '}
        <strong>2.8&ndash;13 mg/kg</strong>. It was indicated in a further 15
        samples at 0.8&ndash;11 mg/kg, though those did not satisfy all five of
        the confirmation criteria the authors applied.
      </p>
      <p>
        <strong>Every result — confirmed and unconfirmed — fell below the
        European specific migration limit of 15 mg/kg.</strong> The average
        across all 50 samples, counting non-detects at half the limit of
        detection, was <strong>2.6 mg/kg</strong> with a standard deviation of
        3.1. Quoting the top of the range without that compliance result would
        be technically accurate and thoroughly misleading, which is why it
        appears here attached to it.
      </p>
      <p>
        The finding that transfers most directly to a liner is this one:{' '}
        <strong>every sample with detectable caprolactam migration came from an
        application involving heating the food in the packaging.</strong>{' '}
        Sausage meat heat-processed in nylon casing. Pouches boiled, microwaved
        or roasted. In one case, half of the extractable caprolactam moved out
        of a nylon-6 sausage casing into the scalded sausage, arriving at
        4.2&nbsp;mg/kg.
      </p>
      <p>
        Cold storage in nylon did not produce detectable migration. Heat inside
        the pack did. A slow cooker liner is a heat-inside-the-pack application
        by definition.
      </p>

      <h2>Where the analogy runs out</h2>
      <p>
        Two variables in a slow cooker sit outside anything in that survey, and
        honesty requires flagging both rather than extrapolating through them.
      </p>
      <p>
        <strong>Duration.</strong> Scalding a sausage takes minutes. Boiling a
        pouch takes minutes. A slow cooker runs six to ten hours, sometimes
        overnight. Migration is a diffusion process and time is one of its
        inputs, so the honest position is that the published numbers describe a
        much shorter exposure than the one you are asking about.
      </p>
      <p>
        <strong>Fat and acid.</strong> The foods that end up in slow cookers —
        chilli, pulled pork, tomato-based stews, curry — are fatty and mildly
        acidic, and both push migration upward for most polymers. A separate
        strand of the caprolactam literature deals specifically with packaging
        intended for fatty foods, which tells you the industry considers fat the
        harder case.
      </p>
      <p>
        Set against that, one variable runs the other way:{' '}
        <strong>a slow cooker is not hot.</strong> On high it sits near
        100&nbsp;°C against a film rated to roughly 204&nbsp;°C, and a good deal
        of the published nylon migration work involves roasting temperatures far
        above anything a crock will reach. It is a long, warm, greasy exposure
        rather than a short, extreme one, and there is no published data on that
        exact combination.
      </p>

      <h2>The crock is not the problem</h2>
      <p>
        Worth saying clearly, because a page like this can easily read as an
        argument against slow cooking: the appliance is one of the better
        surfaces in a kitchen.
      </p>
      <p>
        A slow cooker insert is <strong>glazed ceramic or stoneware</strong> — a
        vitrified, non-porous surface that does not shed polymer, does not have
        a nonstick coating to degrade, and does not care about acid. It is the
        same reason ceramic ranks where it does in our{' '}
        <Link href="/blog/best-non-toxic-cookware" className="text-forest underline">cookware guide</Link>.
        If you cook directly in the crock, there is no plastic in the food path
        at all. The lid gasket, where one exists, is silicone rather than a
        polyolefin, and it sits above the food line.
      </p>
      <p>
        Which puts the whole question in proportion. The liner is an accessory
        sold to save you washing up. Skipping it removes the entire uncertainty
        for the price of a soak.
      </p>

      <h2>How the options compare</h2>

      <BlogComparisonTable
        caption="Slow cooking, ranked by what contacts the food"
        columns={[
          { key: 'method', label: 'Method' },
          { key: 'contact', label: 'Food-contact surface' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { method: 'Cook directly in the ceramic crock', contact: 'Glazed stoneware', verdict: 'No polymer in the food path. The default answer, and it is free.' },
          { method: 'Crock plus a soak and a sponge', contact: 'Glazed stoneware', verdict: 'The liner exists to avoid ten minutes of washing up. Worth knowing that is the whole trade.' },
          { method: 'Nylon slow cooker liner', contact: 'Polyamide film, grade undisclosed', verdict: 'Purpose-built and far from its heat limit. Monomer migration is the open question and the specific case is unmeasured.' },
          { method: 'Oven bag used as a liner', contact: 'Polyamide film', verdict: 'Same family, but Reynolds states its liners are tested only for liner use. Do not treat the products as interchangeable.' },
          { method: 'Any polyethylene bag (Ziploc, freezer, produce)', contact: 'Polyethylene', verdict: 'Not a cooking film. Wrong material for sustained heat — this is the actual bad idea.' },
          { method: 'Slow cooker with a nonstick-coated metal insert', contact: 'Fluoropolymer coating', verdict: 'A different question entirely, and one about coating wear rather than migration.' },
        ]}
      />

      <h2>What we would actually do</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Default to no liner.</strong> Not from alarm, but because the
          uncertainty is free to eliminate and the benefit it buys is a rinse.
          If a liner is what makes the appliance get used on a weeknight, that
          is a real benefit and it counts.
        </li>
        <li>
          <strong>Do not substitute a food storage bag.</strong> This is the one
          genuinely wrong move available. Polyethylene is not a cooking film,
          and the whole reason liners are nylon is that the cheap alternative
          does not hold.
        </li>
        <li>
          <strong>Follow the lifting instruction.</strong> Reynolds says never
          to lift the liner out with food in it. That is about seams and hot
          liquid, and it is the most likely way this product actually hurts
          someone.
        </li>
        <li>
          <strong>Do not store leftovers in the liner.</strong> Decant into
          glass or steel once it has cooled. Long contact with fatty food is the
          variable nobody has measured, and there is no reason to add hours to
          it after the cooking is done.
        </li>
        <li>
          <strong>Do not re-use one.</strong> They are single-use films and a
          second cycle stacks another six hours onto material that has already
          been through it.
        </li>
        <li>
          <strong>Ignore &ldquo;BPA-free&rdquo; on this product.</strong> It is
          true and it is empty. Nylon was never a bisphenol A material, so the
          claim rules out something that was never in play.
        </li>
      </ul>

      <p>
        Related reading:{' '}
        <Link href="/blog/microplastics-in-sous-vide" className="text-forest underline">microplastics in sous vide</Link>,{' '}
        <Link href="/blog/are-vacuum-sealer-bags-safe-microplastics" className="text-forest underline">vacuum sealer bags</Link>,{' '}
        <Link href="/blog/do-ziploc-bags-release-microplastics" className="text-forest underline">do Ziploc bags release microplastics</Link>,{' '}
        <Link href="/blog/what-does-microwave-safe-actually-mean" className="text-forest underline">what &ldquo;microwave safe&rdquo; means</Link>,{' '}
        <Link href="/materials/nylon" className="text-forest underline">nylon as a material</Link>, and{' '}
        <Link href="/products/slow-cooker-liner" className="text-forest underline">our slow cooker liner entry</Link>.
      </p>

      <BlogAppCTA
        headline="Check what is touching the food, not what is on the box"
        sub="Scan cookware, liners and food storage with MicroPlastics for a 0&ndash;100 score weighing material, heat and contact time &mdash; including the long, warm, greasy cases most labels ignore."
        buttonLabel="Scan my kitchen"
      />

      <BlogFAQ
        items={[
          {
            question: 'Do slow cooker liners leach microplastics into food?',
            answer: 'No published study has measured particle release from a slow cooker liner, so any particles-per-serving figure you see has been invented. The documented question for nylon films is migration of leftover monomer rather than shedding of particles. That is a different mechanism with a different regulatory limit, and it is the one worth asking about.',
          },
          {
            question: 'What are slow cooker liners made of?',
            answer: 'Nylon — a polyamide, not polyethylene. Reynolds states first-party that its liners are made of “a blend of nylon” that the company has used for over 30 years, and that they are BPA-free and FDA-compliant for cooking. It is the same polymer family as a turkey oven bag, chosen because nylon tolerates heat that food storage films cannot.',
          },
          {
            question: 'Is a slow cooker liner the same as an oven bag?',
            answer: 'Same material family, but Reynolds explicitly distinguishes them, stating the liners are “specifically tested and designed to guarantee performance only when used as a slow cooker liner.” The common claim that they are identical products in different sizes is not what the manufacturer says, so they should not be treated as interchangeable.',
          },
          {
            question: 'What is caprolactam and should I worry about it?',
            answer: 'Caprolactam is the monomer nylon 6 is polymerised from, and residues can migrate from the film into food. The EU sets a specific migration limit of 15 mg/kg for it. In a survey of 50 retail foods packaged in nylon-6, caprolactam was confirmed in nine at 2.8–13 mg/kg, averaging 2.6 mg/kg across all 50, and every result was below the limit. It applies only if the liner is nylon 6 — nylon 66 contains no caprolactam at all.',
          },
          {
            question: 'Does it matter which type of nylon the liner is?',
            answer: 'Substantially, and this is the gap in the available information. Nylon 6 is made from caprolactam, which has an EU migration limit. Nylon 66 is made from hexamethylenediamine and adipic acid, although small caprolactam residues have been measured in nylon-66 kitchenware, and both types release cyclic oligomers into hot food. Manufacturers say “nylon” without specifying the grade, so you cannot tell which migration question applies to the box in your cupboard. Anyone quoting a confident figure is assuming an answer they have not disclosed.',
          },
          {
            question: 'Can I use a Ziploc bag instead of a slow cooker liner?',
            answer: 'No, and this is the one clearly wrong option. Food storage bags are polyethylene, which is not a cooking film and is not made for sustained heat. The reason liners are nylon in the first place is that polyethylene does not hold up to the application. Substituting one is the version of this that genuinely is a bad idea.',
          },
          {
            question: 'Is the slow cooker itself a problem?',
            answer: 'No. The insert is glazed ceramic or stoneware — vitrified, non-porous, no coating to wear off and indifferent to acid. It is one of the more inert food-contact surfaces in a kitchen. Cook directly in the crock and there is no plastic in the food path at all, which is why the practical answer here is simply to skip the accessory.',
          },
          {
            question: 'Does cooking for eight hours make migration worse?',
            answer: 'Probably, and nobody has measured it. Migration is diffusion-driven, so time is one of its inputs, and the published nylon analogues all involve minutes of heating rather than hours. Fat and acid — chilli, pulled pork, tomato stews — also push migration upward. Running the other way, a slow cooker only reaches about 100 °C against a film rated near 204 °C. That exact combination of long, warm and greasy has not been tested.',
          },
          {
            question: 'Can I store leftovers in the liner?',
            answer: 'Decant them instead, into glass or stainless steel. Reynolds says leftovers should only stay in the liner while it is still in the slow cooker bowl, and that the liner should never be lifted out with food inside — that is a structural warning about seams and hot liquid. Beyond the safety point, extended contact between a polyamide film and fatty food is the untested variable, and there is no reason to extend it after cooking.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Reynolds Brands', year: 2026, title: 'Slow Cooker Liners — manufacturer product information and FAQ. Verbatim: "The liners are made of a special BPA-free material suitable for cooking foods in slow cookers on high, low, or warm settings. Reynolds has safely used this blend of nylon for over 30 years"; "Slow Cooker Liners are BPA-free and FDA-compliant for cooking"; "At no time should the liner be lifted from the bowl with food inside"; liners "specifically tested and designed to guarantee performance only when used as a slow cooker liner". Product specification states a 400 °F heat rating and food-safe nylon blend material', publisher: 'Reynolds Brands (manufacturer, first-party)', url: 'https://www.reynoldsbrands.com/products/cooking-bag-liners/slow-cooker-liners' },
          { authors: 'Barkby CT, Lawson G, et al.', year: 2004, title: 'Method of test and survey of caprolactam migration into foods packaged in nylon-6 — 50 retail foodstuffs analysed; caprolactam confirmed in 9 of 50 at 2.8–13 mg/kg and indicated in a further 15 at 0.8–11 mg/kg; all levels below the European specific migration limit of 15 mg/kg; mean across all 50 samples 2.6 mg/kg (SD 3.1) with non-detects set at half the limit of detection; all samples with detectable migration were applications involving heating the food in the packaging; 50% of extractable caprolactam migrated from a PA6 sausage casing into scalded sausage at 4.2 mg/kg', publisher: 'Food Additives & Contaminants 21(12)', url: 'https://pubmed.ncbi.nlm.nih.gov/15799563/' },
          { authors: 'European Commission', year: 2011, title: 'Commission Regulation (EU) No 10/2011 on plastic materials and articles intended to come into contact with food — specific migration limit for caprolactam (CAS 105-60-2) set at 15 mg/kg of food', publisher: 'EUR-Lex', url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02011R0010-20200923' },
          { authors: 'Félix JS, Isella F, Bosetti O, Nerín C', year: 2010, title: 'Migration of ε-caprolactam residues in packaging intended for contact with fatty foods — establishes fatty-food contact as the harder migration case for polyamide packaging', publisher: 'Food Chemistry / ScienceDirect', url: 'https://www.sciencedirect.com/science/article/pii/S095671351000294X' },
          { authors: 'Mutsuga M, Yamaguchi M, Ohno H, Kawamura Y', year: 2010, title: 'Migration of monomers and primary aromatic amines from nylon products — all nylon 66 samples contained a small amount of ε-caprolactam', publisher: 'Shokuhin Eiseigaku Zasshi 51(5):228–236', url: 'https://pubmed.ncbi.nlm.nih.gov/21071906/' },
          { authors: 'German Federal Institute for Risk Assessment (BfR)', year: 2019, title: 'Polyamide kitchen utensils: keep contact with hot food as brief as possible — Opinion No 036/2019 (5 mg/kg group value for cyclic PA6 and PA66 oligomers)', publisher: 'BfR', url: 'https://www.bfr.bund.de/cm/349/polyamide-kitchen-utensils-keep-contact-with-hot-food-as-brief-as-possible.pdf' },
        ]}
      />
    </BlogPostLayout>
  )
}
